import React from 'react'
import PropTypes from 'prop-types'
import FieldSelectorContainer from '../../containers/EditorContainers/FieldSelectorContainer'
import EditorContainer from '../../containers/EditorContainers/EditorContainer'
import { UIStyles, surfaces } from '../../theme'
import { POLYGON_DRAW, CIRCLE_DRAW, POLYGON_SLICE, POLYGON_MOVE, MARKER_TOOL } from '../../actions/editor'
import OlMap from 'ol/Map'
import { defaults } from 'ol/interaction'
import MouseTooltip from '../UIComponents/MouseTooltip'
import SamplesLayerInteractionContainer from '../../containers/olContainers/SamplesLayerInteractionContainer'
import MapClickInteractionContainer from '../../containers/olContainers/MapClickInteractionContainer'
import SamplesHoverInteractionContainer from '../../containers/olContainers/SamplesHoverInteractionContainer'
import { CLUSTER_ZOOM_THRESHOLD } from '@layers-frontend/commons/constants'

import get from 'lodash/get'
import map from 'lodash/map'
import isEqual from 'lodash/isEqual'
import GeometryUtils from '../../geometryUtils'
import { getFieldViewFit, hasExtent } from './fieldViewFit'

// Same look as the MUI tooltips.
const tooltipStyle = {
  padding: '10px',
  backgroundColor: UIStyles.deepblueColor3,
  color: UIStyles.whiteColor,
  borderRadius: '8px',
  boxShadow: surfaces.mapPanelShadow
}

// Size of padding and selected fields aside
const DEFAULT_PADDING = 20
const SELECTED_FIELDS_ASIDE = 420
const DEFAULT_PADDING_WHEN_FIELD_VIEW_ACTIVE = [DEFAULT_PADDING, DEFAULT_PADDING, DEFAULT_PADDING, SELECTED_FIELDS_ASIDE]

export default class Map extends React.Component {
  constructor(props) {
    super(props)
    this.map = new OlMap({
      loadTilesWhileAnimating: props.loadTilesWhileAnimating,
      loadTilesWhileInteracting: props.loadTilesWhileInteracting,
      interactions: props.useDefaultInteractions ? defaults() : [],
      controls: []
    })

    this.state = {
      initMap: false,
      cursor: 'default',
      sumatoryCoords: '',
      isVisibleNameFieldTooltip: false,
      fieldNameWithMouseOver: '',
      shouldFitEditorGeometry: true
    }

    // A field view fit waiting for its fields to be drawn (fitFieldView). Not state: it never re-renders the map.
    this.isFieldViewFitPending = false

    if (props.onChangeSize) {
      this.map.on('change:size', props.onChangeSize)
    }
  }

  selectPointerMove = event => {
    let fieldName = null
    const { isSearchView, isFieldView, isEditorMeasureToolActive, isGlobalLayerView, zoomLevel, t } = this.props
    const isFieldNameTooltipActive = (isSearchView || isFieldView) && !isEditorMeasureToolActive
    if (isGlobalLayerView && zoomLevel < CLUSTER_ZOOM_THRESHOLD) {
      this.setState({ cursor: 'default', isVisibleNameFieldTooltip: false, fieldNameWithMouseOver: fieldName })
      return
    }
    if (isFieldNameTooltipActive) {
      const mouseCoordInMapPixels = this.map.getEventPixel(event.originalEvent)

      // detect feature at mouse coords and return it
      const feature = this.map.forEachFeatureAtPixel(mouseCoordInMapPixels, f => f)

      let tooltipText = ''
      if (feature) {
        const fieldFeatures = feature.get('field')
        if (fieldFeatures && fieldFeatures.externalId) {
          // sample pin
          const sample = fieldFeatures
          const sampleType = sample.type?.name ? t(sample.type.name) : t('Unknown')
          const sampleDate = sample.sampleCreationDate ? new Date(sample.sampleCreationDate).toLocaleDateString() : t('No date')
          const sampleId = sample.uniqueId || sample.externalId || t('No ID')
          tooltipText = `${sampleType} - ${sampleDate} - ${sampleId}`
        } else {
          // field geometry
          const fieldId = get(fieldFeatures, 'fieldId') || get(fieldFeatures, 'gm_field_id') || get(fieldFeatures, 'id')
          const field = this.props.getFieldById(fieldId)
          fieldName = get(field, 'name') || get(fieldFeatures, 'name') || get(fieldFeatures, 'gm_name')
          if (fieldFeatures?.name === '' || fieldFeatures?.gm_name === '') {
            fieldName = this.props.t('nameless field')
          }
          tooltipText = fieldName || ''
        }
      }

      const hit = !!feature
      const newState = {
        cursor: hit ? 'pointer' : 'default',
        isVisibleNameFieldTooltip: hit,
        fieldNameWithMouseOver: tooltipText
      }

      if (
        this.state.cursor !== newState.cursor ||
        this.state.isVisibleNameFieldTooltip !== newState.isVisibleNameFieldTooltip ||
        this.state.fieldNameWithMouseOver !== newState.fieldNameWithMouseOver
      ) {
        this.setState(newState)
      }
    } else {
      this.setState({ cursor: 'default', isVisibleNameFieldTooltip: false, fieldNameWithMouseOver: fieldName })
    }
  }

  componentDidMount() {
    this.map.setTarget(this.refs.target)

    this.map.on('pointermove', this.selectPointerMove)
    this.map.on('postrender', this.fitPendingFieldView)

    this.map.on('moveend', () => {
      const currentZoom = this.props.zoomLevel
      const newZoom = this.map.getView().getZoom()
      const roundedCurrentZoom = Math.round(currentZoom * 100) / 100
      const roundedNewZoom = Math.round(newZoom * 100) / 100
      const isZoom = roundedCurrentZoom !== roundedNewZoom
      const disableZoomRegister = roundedCurrentZoom >= CLUSTER_ZOOM_THRESHOLD && roundedNewZoom >= CLUSTER_ZOOM_THRESHOLD
      if (!currentZoom) this.props.setZoomLevel(roundedNewZoom)
      if (isZoom && !disableZoomRegister) {
        this.props.setZoomLevel(roundedNewZoom)
      }
    })

    /* center map with boundaires element after init */

    if (this.props.focusOnMount) {
      this.focus()
    }

    this.fitFieldView({ wasFieldView: false })
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // reset curor when comming from editor
    if (!nextProps.isSearchView) {
      this.setState({ cursor: 'default' })
    }

    /*** center all fields on init ***/
    const clusterLayer = this.constructor.getLayerFromName('cluster_layer', this.map)
    const featuresLayer = this.constructor.getLayerFromName('geometries', this.map)
    if (clusterLayer && !this.state.initMap) {
      const clusterSource = clusterLayer.getSource()

      this.zoomToLayer(clusterSource)
      this.setState({ initMap: true })
    }

    // fix extent to currently filtred fields
    const hasFiltersUpdate = this.props.filters !== nextProps.filters
    const hasSearchInputUpdate = this.props.searchInput !== nextProps.searchInput && nextProps.searchInput !== ''
    if (hasFiltersUpdate || hasSearchInputUpdate) {
      // hack to fix zoomToLayer delay
      setTimeout(() => {
        const filteredFeaturesExtent = GeometryUtils.boundingBoxFromFeatures(nextProps.filteredFeatures)
        if (!filteredFeaturesExtent.includes(Infinity) || !filteredFeaturesExtent.includes(NaN)) {
          this.zoomToBbox(filteredFeaturesExtent)
        }
      }, 100)
    }

    // fit geometry on editor interaction
    if (this.state.shouldFitEditorGeometry && nextProps.isEditorActive && nextProps.isFieldSeasonFormEditorOpen) {
      const editorGeo = this.constructor.getLayerFromName('editor', this.map)
      if (editorGeo) {
        this.setState({ shouldFitEditorGeometry: false }, () => this.zoomToLayer(editorGeo))
      }
    }

    if (!nextProps.isFieldSeasonFormEditorOpen && !this.state.shouldFitEditorGeometry) {
      this.setState({ shouldFitEditorGeometry: true })
    }

    if (nextProps.isFieldView) {
      // check if new geometries extent is different then old
      const oldGeometriesArray = map(this.props.selectedFlightGroupSeasons, season => get(season, 'geometry'))
      const newGeometriesArray = map(nextProps.selectedFlightGroupSeasons, season => get(season, 'geometry'))
      const oldExtent = get(GeometryUtils.boundingBoxFromGeometries(oldGeometriesArray), 'bbox')
      const newExtent = get(GeometryUtils.boundingBoxFromGeometries(newGeometriesArray), 'bbox')
      if (!isEqual(oldExtent, newExtent)) {
        setTimeout(() => {
          this.zoomToLayer(featuresLayer)
        }, 1)
      }
    }
  }

  componentDidUpdate(prevProps) {
    this.fitFieldView({ wasFieldView: prevProps.isFieldView })
  }

  getChildContext() {
    return { map: this.map }
  }

  componentWillUnmount() {
    this.map.un('pointermove', this.selectPointerMove)
    this.map.un('postrender', this.fitPendingFieldView)
    this.map.setTarget(undefined)
  }

  generateFont = (font, code, position, size) => {
    const canvas = document.createElement('canvas')

    canvas.width = 24
    canvas.height = 24
    // eslint-disable-next-line no-var
    var ctx = canvas.getContext('2d')
    ctx.fillStyle = UIStyles.whiteColor
    // Soft dark halo so the white cursor stays visible over bright imagery.
    ctx.shadowColor = 'rgba(0, 0, 0, 0.6)'
    ctx.shadowBlur = 2
    ctx.font = size + " 24px '" + font + "'"
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(String.fromCharCode(code), 12, 12)
    const dataURL = canvas.toDataURL('image/png', 1)
    return 'url(' + dataURL + ') ' + position + ', auto'
  }

  getCursorStyle = () => {
    const { isEditorActive, activeTool } = this.props
    if (isEditorActive) {
      switch (activeTool) {
        case POLYGON_DRAW:
          return this.generateFont('Font Awesome 5 Free', '0xf303', '0 24', '900')
        case MARKER_TOOL:
          return
        case CIRCLE_DRAW:
          return this.generateFont('Font Awesome 5 Free', '0xf303', '0 24', '900')
        case POLYGON_MOVE:
          return this.generateFont('Font Awesome 5 Free', '0xf0b2', '12 12', '900')
        case POLYGON_SLICE:
          return this.generateFont('Ionicons', '0xf34b', '12 12', '300')
        default:
          return this.generateFont('Font Awesome 5 Free', '0xf303', '0 24', '900')
      }
    } else {
      return this.state.cursor
    }
  }

  render = () => {
    /** if not editor, we render the field selector */
    const { isSearchView, isEditorActive, children, view, zoomLevel, isGlobalLayerView } = this.props
    const fieldSelector = isSearchView && (!isGlobalLayerView || zoomLevel >= CLUSTER_ZOOM_THRESHOLD) ? <FieldSelectorContainer /> : null
    return (
      <div style={{ cursor: this.getCursorStyle() }}>
        <div ref="target">
          {isEditorActive && <EditorContainer />}
          {fieldSelector}
          <SamplesLayerInteractionContainer />
          <MapClickInteractionContainer />
          {!isEditorActive && <SamplesHoverInteractionContainer />}
        </div>
        <div>
          {children}
          {view}
        </div>
        {this.state.fieldNameWithMouseOver && (
          <MouseTooltip style={tooltipStyle} visible={this.state.isVisibleNameFieldTooltip} offsetX={15} offsetY={10}>
            <span>{this.state.fieldNameWithMouseOver}</span>
          </MouseTooltip>
        )}
      </div>
    )
  }

  /*=============================================>>>>>
  = Map utilities =
  ===============================================>>>>>*/
  /*----------- Map focus -----------*/

  focus() {
    const viewport = this.map.getViewport()
    viewport.tabIndex = 0
    viewport.focus()
  }

  /*----------- Get layer from name -----------*/
  static getLayerFromName(name, map) {
    let returnLayer
    map
      .getLayers()
      .getArray()
      .forEach(function (layer) {
        if (layer.get('name') === name) {
          returnLayer = layer
        }
      })
    return returnLayer
  }

  /* ----------- zoom to layer ----------- */
  zoomToBbox(bbox) {
    if (bbox && bbox[0] !== Infinity) {
      const view = this.map.getView()
      view.fit(bbox, { duration: 750, maxZoom: 16 })
    }
  }

  /* ----------- zoom to layer ----------- */
  zoomToLayer(layer, isGroup = false) {
    const boundingExtent = isGroup ? layer.getExtent() : layer?.getSource()?.getExtent()
    if (boundingExtent && boundingExtent[0] !== Infinity) {
      const view = this.map.getView()
      const padding = this.props.isFieldView ? DEFAULT_PADDING_WHEN_FIELD_VIEW_ACTIVE : undefined
      view.fit(boundingExtent, { duration: 750, maxZoom: 20, padding })
    }
  }

  /* ----------- fit the field view to its fields ----------- */
  // As it opens or, when it opened before the fields layer existed, once the fields are drawn (getFieldViewFit).
  fitFieldView({ wasFieldView }) {
    const featuresLayer = this.constructor.getLayerFromName('geometries', this.map)
    const { shouldFit, isFitPending } = getFieldViewFit({
      isFieldView: this.props.isFieldView,
      wasFieldView,
      isFitPending: this.isFieldViewFitPending,
      hasFieldsLayer: !!featuresLayer,
      hasFields: hasExtent(featuresLayer?.getSource()?.getExtent())
    })
    this.isFieldViewFitPending = isFitPending
    if (shouldFit) {
      this.zoomToLayer(featuresLayer)
    }
  }

  // The fields layer's container is connected below the map, so it redraws after the map's update (react-redux
  // notifies nested subscribers once the parent has committed): the first frame that draws the fields fits a field
  // view still waiting. A frame without a size (no frameState) has nothing to fit into.
  fitPendingFieldView = event => {
    if (this.isFieldViewFitPending && event.frameState) {
      this.fitFieldView({ wasFieldView: this.props.isFieldView })
    }
  }

  /* ----------- center map to feature ----------- */
  zoomToFeature(feature) {
    this.map.getView().fit(feature.getGeometry().getExtent(), this.map.getSize())
  }

  /* ----------- Get feature by id ----------- */

  getFeatureById(featureId, layer) {
    return this.map.getLayers().getArray()[layer].getSource().getFeatureById(featureId)
  }
  /* = End of Map utilities = */
  /* =============================================<<<<< */
}

// types
Map.propTypes = {
  activeTool: PropTypes.string,
  dispatch: PropTypes.func,
  focusOnMount: PropTypes.bool.isRequired,
  isAllActive: PropTypes.bool,
  isEditorActive: PropTypes.bool,
  isFetchedData: PropTypes.bool,
  isTemporalView: PropTypes.bool,
  selectedField: PropTypes.object,
  useDefaultInteractions: PropTypes.bool.isRequired,
  useDefaultControls: PropTypes.bool.isRequired,
  loadTilesWhileAnimating: PropTypes.bool,
  loadTilesWhileInteracting: PropTypes.bool,
  onSingleClick: PropTypes.func,
  onChangeSize: PropTypes.func,
  view: PropTypes.element.isRequired,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element]),
  t: PropTypes.func.isRequired
}

Map.defaultProps = {
  useDefaultInteractions: true,
  useDefaultControls: true,
  focusOnMount: false
}

Map.childContextTypes = {
  map: PropTypes.instanceOf(OlMap)
}
